// EVA-253 Password strength scorer — penalised segment entropy.
//
// DOM-free and dependency-free on purpose: package.json declares
// "type": "module", so `spec/javascript/password_strength_spec.rb` imports this
// exact file into Node and drives it against spec/fixtures/password_strength/
// golden.json. There is no second implementation to drift from.
//
// The score is ADVISORY. It never gates submission — the EVA-229 policy does
// (see password_policy_controller.js). Scoring is entirely local; the password
// is never transmitted anywhere.
//
// Known limitations, stated rather than hidden:
//   1. A word outside COMMON_TOKENS scores as random ("Marmalade", "Troubadour"
//      over-score). We over-praise a mediocre password; we never under-praise a
//      good one, and no top-100 leaked pattern can read as strong.
//   2. Token matching is substring-anchored, so a token inside a longer word
//      discounts it ("cassette" hits "asset"). That errs toward under-scoring.
//   3. Dictionary-grade accuracy belongs on the server at submit time, where
//      the corpus costs zero client bytes — not in 230 KB on the login path.

const LEET = {
  "4": "a", "3": "e", "1": "i", "0": "o", "5": "s",
  "7": "t", "@": "a", "$": "s", "!": "i", "|": "l"
}

const SEQUENCES = [
  "abcdefghijklmnopqrstuvwxyz",
  "0123456789",
  "qwertyuiop",
  "asdfghjkl",
  "zxcvbnm"
]

// Data, not user-visible copy — deliberately NOT in i18n. ~120 tokens covering
// the classes that dominate corporate password audits: leaked top-100, months,
// weekdays, seasons, UK place names, generic office/product vocabulary.
// Per-user contextual tokens (employer, name, email) are injected at runtime
// from PasswordStrength.context_tokens.
export const COMMON_TOKENS = Object.freeze([
  "password", "passwd", "pass", "welcome", "letmein", "login", "admin", "administrator", "root", "user",
  "guest", "test", "testing", "demo", "temp", "temporary", "changeme", "default", "secret", "access",
  "qwerty", "qwertyuiop", "asdfgh", "zxcvbn", "abc123", "iloveyou", "monkey", "dragon", "master", "shadow",
  "sunshine", "princess", "football", "baseball", "soccer", "cricket", "hockey", "superman", "batman", "starwars",
  "pokemon", "trustno", "whatever", "freedom", "hunter", "ranger", "harley", "ginger", "jordan", "michael",
  "jennifer", "charlie", "robert", "thomas", "daniel", "matthew", "andrew", "joshua", "hello", "summer",
  "winter", "spring", "autumn", "january", "february", "march", "april", "june", "july", "august",
  "september", "october", "november", "december", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday",
  "sunday", "london", "england", "britain", "scotland", "wales", "manchester", "liverpool", "chelsea", "arsenal",
  "company", "office", "work", "business", "finance", "invoice", "report", "valuation", "property", "portfolio",
  "asset", "fund", "tenant", "lease", "rent", "market", "capital", "invest", "client", "project",
  "google", "facebook", "amazon", "microsoft", "apple", "windows", "internet", "computer", "mobile", "phone",
  "money", "secure", "security", "private", "system", "server", "network", "database", "backup"
])

const DICT_BITS = 6      // a token from a ~120-word list ≈ 2^6 guesses
const YEAR_BITS = 7      // ~200 plausible years
const SEQUENCE_BITS = 3  // "abc", "123", "qwe" — direction + start
const REPEAT_BITS = 2    // "aaa"
const MIN_TOKEN = 4
const MIN_RUN = 3

// Additive character pool. Letters are counted ONCE per case class; the
// positional discounts in scoreBits() price the "capital at the front" and
// "digits at the end" shapes, which is what a naive pool calculation misses.
function unitBits(password) {
  let pool = 0
  if (/[a-z]/.test(password)) pool += 26
  if (/[A-Z]/.test(password)) pool += 26
  if (/[0-9]/.test(password)) pool += 10
  if (/[ -\/:-@\[-`{-~]/.test(password)) pool += 33
  if (/[^\x00-\x7F]/.test(password)) pool += 100
  return Math.log2(Math.max(2, pool))
}

// De-leeting is for DICTIONARY matching only. Applying it before sequence
// detection turns "123" into "ie3" and lets Password123! score 29 instead of
// 12.6 — this two-view split is load-bearing, do not collapse it.
function deleet(chars) {
  return chars.map((char) => LEET[char] || char)
}

function sequenceRun(lowered, i) {
  let best = 0
  for (const row of SEQUENCES) {
    const start = row.indexOf(lowered[i])
    if (start < 0) continue
    for (const direction of [1, -1]) {
      let n = 1
      while (i + n < lowered.length) {
        const at = start + direction * n
        if (at < 0 || at >= row.length || row[at] !== lowered[i + n]) break
        n++
      }
      if (n > best) best = n
    }
  }
  return best
}

// Greedy left-to-right: at each index take the LONGEST match among token, year,
// sequence run and repeat run; otherwise charge one character at pool rate.
//
// `chars` is an array of CODE POINTS, never a string indexed by code unit. With
// code units a surrogate pair is two indices that differ from each other, so
// "😀😀😀😀😀😀Aa1!" — two distinct characters — defeated repeat detection
// entirely and scored 121 bits / Excellent.
function scan(chars, unit, tokens) {
  const lowered = chars.map((char) => char.toLowerCase())
  const deleeted = deleet(lowered)
  let bits = 0
  let i = 0

  while (i < chars.length) {
    let tokenLength = 0
    for (const token of tokens) {
      if (token.length >= MIN_TOKEN && token.length > tokenLength &&
          deleeted.slice(i, i + token.length).join("") === token) {
        tokenLength = token.length
      }
    }

    const isYear = /^(19\d\d|20[0-3]\d)/.test(chars.slice(i, i + 4).join(""))
    const seqLength = sequenceRun(lowered, i)

    let repeatLength = 1
    while (i + repeatLength < lowered.length && lowered[i + repeatLength] === lowered[i]) repeatLength++

    const matches = []
    if (tokenLength >= MIN_TOKEN) matches.push([tokenLength, DICT_BITS])
    if (isYear) matches.push([4, YEAR_BITS])
    if (seqLength >= MIN_RUN) matches.push([seqLength, SEQUENCE_BITS])
    if (repeatLength >= MIN_RUN) matches.push([repeatLength, REPEAT_BITS])

    if (matches.length === 0) {
      bits += unit
      i += 1
      continue
    }

    matches.sort((a, b) => b[0] - a[0])
    bits += matches[0][1]
    i += matches[0][0]
  }

  return bits
}

export function scoreBits(password, extraTokens = []) {
  if (!password) return 0

  const tokens = extraTokens.length ? COMMON_TOKENS.concat(extraTokens) : COMMON_TOKENS
  const unit = unitBits(password)
  const chars = Array.from(password)   // code points, not UTF-16 code units
  const length = chars.length
  let bits = scan(chars, unit, tokens)

  // Repeated-block collapse. Without it "Ab1!Ab1!Ab1!Ab1!" scores 78.8 / Strong.
  for (let blockLength = 1; blockLength <= length / 2; blockLength++) {
    if (length % blockLength !== 0) continue
    const block = chars.slice(0, blockLength)
    const repeats = length / blockLength
    if (chars.some((char, i) => char !== block[i % blockLength])) continue
    const collapsed = scan(block, unit, tokens) + Math.log2(repeats) + 1
    if (collapsed < bits) bits = collapsed
  }

  // Positional decoration discounts: capitalising the first letter and hanging
  // the digits and symbols off the end is what literally everyone does.
  const uppers = []
  for (let i = 0; i < length; i++) {
    if (chars[i] >= "A" && chars[i] <= "Z") uppers.push(i)
  }
  if (uppers.length > 0 && uppers.every((i) => i === 0)) bits -= 1

  let tailLength = 0
  while (tailLength < length && !/[A-Za-z]/.test(chars[length - 1 - tailLength])) tailLength++
  if (tailLength > 0) {
    const head = chars.slice(0, length - tailLength)
    if (head.length > 0 && head.every((char) => /[A-Za-z]/.test(char))) bits -= 2
  }

  return Math.max(0, bits)
}

// buckets: PasswordStrength::BUCKETS, ascending by min_bits.
export function bucketFor(bits, buckets) {
  let code = buckets[0].code
  for (const bucket of buckets) {
    if (bits >= bucket.min_bits) code = bucket.code
  }
  return code
}
